import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from '../../utils/date'
import {
    formatDiff,
    formatDiffFloor,
    formatNumber,
    formatUSD,
    formatWAX,
    getOwnerLink,
} from '../helpers/FormatLinks'
import { Context } from '../waxplorer/Waxplorer'
import { Accordion, AccordionItem } from '@nextui-org/react'
import PopupWrapperSearch from '../popups/PopupWrapperSearch'

export const MarketInfoDetails = ({ asset, listing, cancel, className }) => {
    let { template } = asset
    if (!template) {
        template = listing?.template
    }

    const {
        waxPrice,
        usdPrice,
        price,
        currency,
        usdWax,
        market,
        buyer,
        seller,
        date,
    } = listing
        ? listing
        : {
              assets: null,
              price: null,
              currency: null,
              usdWax: null,
              market: null,
              buyer: null,
              seller: null,
              date: null,
          }

    const avgWAX = template?.stats.averageWaxPrice
    const avgUSD = template?.stats.averageUsd
    const lastSold = template?.stats.lastSold?.priceWax
    const lastSoldUSD = template?.stats.lastSold?.priceUsd
    const lowestWAX = template?.stats.floorPrice
    const numSales = template?.stats.numSales

    let diffMedian = 0
    if (price && currency === 'WAX' && avgWAX) diffMedian = price / avgWAX - 1
    if (price && currency === 'USD' && avgUSD) diffMedian = price / avgUSD - 1

    let diffLastSold = 0
    if (price && currency === 'WAX' && lastSold)
        diffLastSold = price / lastSold - 1
    if (price && currency === 'USD' && lastSoldUSD)
        diffLastSold = price / lastSoldUSD - 1

    let diffFloor = 0
    if (price && currency === 'WAX' && lowestWAX) diffFloor = price - lowestWAX
    if (price && currency === 'USD' && lowestWAX)
        diffFloor = price / usdWax - lowestWAX

    const { t } = useTranslation('common')

    const dateStr = format(date, null)

    const initialOpenItems = () => {
        const openItems = new Map()
        openItems.set(t('search.market'), true)
        return openItems
    }

    const [toggleItem, setToggleItem] = useState(initialOpenItems())

    const collapsibleListItem = (name, children) => {
        return (
            <>
                <Accordion className="px-0" defaultExpandedKeys={['2']}>
                    <AccordionItem
                        classNames={{
                            trigger:
                                'px-4 py-2 my-1 rounded-lg dark:bg-zinc-800 bg-zinc-200 font-semibold',
                        }}
                        title={name}
                        toggleItem={toggleItem}
                        setToggleItem={setToggleItem}
                        key="2"
                    >
                        <div className="mb-5">{children}</div>
                    </AccordionItem>
                </Accordion>
            </>
        )
    }

    const [searchType, setSearchType] = useState(null)
    const [searchAsset, setSearchAsset] = useState(null)

    const setPopupSearch = (searchType, asset) => {
        setSearchType(searchType)
        setSearchAsset(asset)
    }

    const [, dispatch] = useContext(Context)
    return buyer && seller && waxPrice ? (
        <div
            className={
                className
                    ? className
                    : 'relative w-full mt-6 max-h-100 overflow-y-auto '
            }
        >
            <PopupWrapperSearch
                asset={searchAsset}
                searchType={searchType}
                setSearchType={setSearchType}
            />
            {collapsibleListItem(
                t('search.market'),
                <Table>
                    <TableBody>
                        {buyer && (
                            <TableRow>
                                <TableCell>Buyer</TableCell>
                                <TableCell>{getOwnerLink(buyer)}</TableCell>
                            </TableRow>
                        )}
                        {seller && (
                            <TableRow>
                                <TableCell>Seller</TableCell>
                                <TableCell>{getOwnerLink(seller)}</TableCell>
                            </TableRow>
                        )}
                        {market && (
                            <TableRow>
                                <TableCell>Market</TableCell>
                                <TableCell>{market}</TableCell>
                            </TableRow>
                        )}
                        {waxPrice && currency === 'WAX' ? (
                            <TableRow>
                                <TableCell>Price</TableCell>
                                <TableCell>{`${formatWAX(
                                    waxPrice,
                                )} WAX / ${formatUSD(usdPrice)}`}</TableCell>
                            </TableRow>
                        ) : (
                            ''
                        )}
                        {dateStr && (
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>{dateStr}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>,
            )}
        </div>
    ) : (
        <div
            className={
                className
                    ? className
                    : 'relative w-full mt-4 max-h-100 overflow-y-auto InsideScroll'
            }
        >
            <PopupWrapperSearch
                asset={searchAsset}
                searchType={searchType}
                setSearchType={setSearchType}
            />
            {collapsibleListItem(
                t('search.market'),
                <Table size="small">
                    <TableBody>
                        {avgWAX && (
                            <TableRow>
                                <TableCell>Median</TableCell>
                                <TableCell>
                                    {
                                        <div className={'flex'}>
                                            {`${formatNumber(
                                                avgWAX,
                                            )} WAX / $${formatNumber(avgUSD)}`}
                                            {formatDiff(
                                                diffMedian,
                                                market,
                                                'Median',
                                            )}
                                        </div>
                                    }
                                </TableCell>
                            </TableRow>
                        )}
                        {lastSold && (
                            <TableRow>
                                <TableCell>{t('asset.last_sold')}</TableCell>
                                <TableCell>
                                    <div
                                        className="flex cursor-pointer"
                                        onClick={() => {
                                            cancel && cancel()
                                            setPopupSearch(
                                                'listings',
                                                {
                                                    collection:
                                                        asset.collection
                                                            .collectionName,
                                                    schema: asset.schema,
                                                    templateId:
                                                        asset.template
                                                            ?.templateId,
                                                },
                                                dispatch,
                                            )
                                        }}
                                    >
                                        {`${formatNumber(
                                            lastSold,
                                        )} WAX / $${formatNumber(lastSoldUSD)}`}
                                        {formatDiff(
                                            diffLastSold,
                                            market,
                                            t('asset.last_sold'),
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {numSales && (
                            <TableRow>
                                <TableCell>Sales</TableCell>
                                <TableCell>{numSales}</TableCell>
                            </TableRow>
                        )}
                        {lowestWAX && (
                            <TableRow>
                                <TableCell>{t('search.cheapest')}</TableCell>
                                <TableCell>
                                    <div
                                        className="flex cursor-pointer"
                                        onClick={() => {
                                            cancel && cancel()
                                            setPopupSearch(
                                                'listings',
                                                {
                                                    collection:
                                                        asset.collection
                                                            .collectionName,
                                                    schema: asset.schema,
                                                    templateId:
                                                        asset.template
                                                            ?.templateId,
                                                },
                                                dispatch,
                                            )
                                        }}
                                    >
                                        {`${formatNumber(
                                            lowestWAX,
                                        )} WAX / $${formatNumber(
                                            lowestWAX * usdWax,
                                        )}`}
                                        {formatDiffFloor(
                                            diffFloor,
                                            market,
                                            'Floor',
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>,
            )}
        </div>
    )
}
